import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SITE_MAP } from '../data/siteMap';
import { loginByEmail } from '../lib/api';
import { useUserStore, roleHome } from '../store/userStore';

const ROLES = ['patient', 'clinician', 'admin'];

function RolePicker({ active, onPick }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3" data-testid="sitemap-role-picker">
      {ROLES.map((k) => {
        const r = SITE_MAP[k];
        const isActive = active === k;
        return (
          <button
            key={k}
            data-testid={`sitemap-role-${k}`}
            onClick={() => onPick(k)}
            className={`text-left rounded-2xl p-5 border transition-base ${isActive ? 'bg-white/10 border-white/30' : 'bg-white/5 border-white/10 hover:bg-white/8'}`}
            style={isActive ? { boxShadow: `0 0 0 2px ${r.color}` } : undefined}
          >
            <div className="grid place-items-center w-11 h-11 rounded-xl mb-3 text-white" style={{ background: r.color }}>
              <i className={`fa-solid ${r.icon}`} />
            </div>
            <div className="font-display text-xl text-white">{r.label}</div>
            <div className="text-sm text-white/55 mt-1">{r.tagline}</div>
            <div className="text-xs text-white/40 mt-3">{r.steps.length} steps · tap to walk through</div>
          </button>
        );
      })}
    </div>
  );
}

function StepPanel({ role, index, onIndex, onTry, onZoom }) {
  const step = role.steps[index];
  const last = index === role.steps.length - 1;
  return (
    <div className="glass-dark rounded-3xl p-6 sm:p-8" data-testid="sitemap-step-panel">
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="sitemap-progress">
        {role.steps.map((s, i) => (
          <button
            key={s.title}
            data-testid={`sitemap-dot-${i}`}
            onClick={() => onIndex(i)}
            title={s.title}
            className="h-2 rounded-full transition-all"
            style={{ width: i === index ? 36 : 12, background: i <= index ? role.color : 'rgba(255,255,255,0.15)' }}
          />
        ))}
        <span className="ml-auto text-xs text-white/50">Step {index + 1} of {role.steps.length}</span>
      </div>

      <div className="flex items-start gap-4 mb-5">
        <div className="grid place-items-center w-14 h-14 rounded-2xl text-white text-xl shrink-0" style={{ background: role.color }}>
          <i className={`fa-solid ${step.icon}`} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-white/40 mb-1">{role.label} · step {index + 1}</div>
          <h2 className="font-display text-2xl sm:text-3xl text-white" data-testid="sitemap-step-title">{step.title}</h2>
        </div>
      </div>

      {step.image ? (
        <button
          type="button"
          data-testid="sitemap-step-image"
          onClick={() => onZoom(step)}
          className="group relative block w-full rounded-2xl overflow-hidden border border-white/10 mb-5 text-left"
          style={{ boxShadow: `0 20px 60px -20px ${role.color}66` }}
        >
          <img src={step.image} alt={`Screenshot: ${step.title}`} className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" />
          <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent">
            <span className="text-xs text-white/80"><i className="fa-solid fa-camera mr-1.5" />Live screenshot from the app</span>
            <span className="text-xs text-white/80 bg-white/10 rounded-full px-2.5 py-1"><i className="fa-solid fa-magnifying-glass-plus mr-1" />Enlarge</span>
          </div>
        </button>
      ) : null}

      <div className="grid md:grid-cols-5 gap-4 mb-6">
        <div className="md:col-span-3 rounded-2xl bg-white/5 border border-white/10 p-5">
          <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2"><i className="fa-solid fa-gear mr-1.5" />How it works</div>
          <p className="text-white/85 leading-relaxed" data-testid="sitemap-step-what">{step.what}</p>
        </div>
        <div className="md:col-span-2 rounded-2xl border p-5" style={{ background: `${role.color}14`, borderColor: `${role.color}40` }}>
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: role.color }}><i className="fa-solid fa-lightbulb mr-1.5" />Why it matters</div>
          <p className="text-white/85 leading-relaxed" data-testid="sitemap-step-why">{step.why}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          data-testid="sitemap-prev"
          onClick={() => onIndex(Math.max(0, index - 1))}
          disabled={index === 0}
          className="rounded-xl px-4 py-2.5 border border-white/15 text-white/80 hover:bg-white/5 disabled:opacity-30 transition-base"
        >
          <i className="fa-solid fa-arrow-left mr-2" />Previous
        </button>
        {!last ? (
          <button
            data-testid="sitemap-next"
            onClick={() => onIndex(index + 1)}
            className="rounded-xl px-5 py-2.5 text-white font-semibold transition-base"
            style={{ background: role.color }}
          >
            Next step<i className="fa-solid fa-arrow-right ml-2" />
          </button>
        ) : (
          <button
            data-testid="sitemap-finish"
            onClick={() => onIndex(0)}
            className="rounded-xl px-5 py-2.5 text-white font-semibold transition-base"
            style={{ background: role.color }}
          >
            <i className="fa-solid fa-rotate-left mr-2" />Start again
          </button>
        )}
        <button
          data-testid="sitemap-try-step"
          onClick={() => onTry(step.route)}
          className="ml-auto rounded-xl px-4 py-2.5 bg-white/10 text-white hover:bg-white/15 transition-base text-sm"
        >
          <i className="fa-solid fa-arrow-up-right-from-square mr-2" />Try this in the live demo
        </button>
      </div>
    </div>
  );
}

function StepOverview({ role, index, onIndex }) {
  return (
    <div className="glass-dark rounded-3xl p-5" data-testid="sitemap-overview">
      <div className="text-xs uppercase tracking-wider text-white/40 mb-3">The {role.label.toLowerCase()} journey</div>
      <ol className="space-y-1.5">
        {role.steps.map((s, i) => (
          <li key={s.title}>
            <button
              data-testid={`sitemap-overview-${i}`}
              onClick={() => onIndex(i)}
              className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2 transition-base ${i === index ? 'bg-white/10' : 'hover:bg-white/5'}`}
            >
              <span className="grid place-items-center w-7 h-7 rounded-lg text-xs font-bold text-white shrink-0" style={{ background: i === index ? role.color : 'rgba(255,255,255,0.12)' }}>{i + 1}</span>
              <span className={`text-sm ${i === index ? 'text-white' : 'text-white/70'}`}>{s.title}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

function WalkthroughVideo({ role }) {
  if (!role.video) return null;
  return (
    <div className="glass-dark rounded-3xl p-5 mb-5" data-testid="sitemap-video">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-display text-lg text-white flex items-center gap-2"><i className="fa-solid fa-circle-play" style={{ color: role.color }} /> Watch the {role.label.toLowerCase()} walkthrough</div>
          <div className="text-xs text-white/50">A recorded click-through of every step below, in order. Each step is captioned on screen — no sound needed.</div>
        </div>
      </div>
      <video
        key={role.key}
        data-testid="sitemap-video-player"
        src={role.video}
        controls
        preload="metadata"
        playsInline
        crossOrigin="anonymous"
        className="w-full rounded-2xl border border-white/10 bg-black"
        style={{ boxShadow: `0 20px 60px -20px ${role.color}66` }}
      >
        {role.captions ? <track kind="subtitles" srcLang="en" label="English" src={role.captions} /> : null}
      </video>
    </div>
  );
}

function Lightbox({ step, onClose }) {
  if (!step) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm grid place-items-center p-4" data-testid="sitemap-lightbox" onClick={onClose}>
      <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-white font-display text-xl">{step.title}</div>
          <button data-testid="sitemap-lightbox-close" onClick={onClose} className="text-white/70 hover:text-white w-9 h-9 grid place-items-center rounded-full bg-white/10"><i className="fa-solid fa-xmark" /></button>
        </div>
        <img src={step.image} alt={step.title} className="w-full h-auto rounded-2xl border border-white/10" />
      </div>
    </div>
  );
}

export default function SiteMap() {
  const navigate = useNavigate();
  const setUser = useUserStore((s) => s.setUser);
  const [params, setParams] = useSearchParams();
  const roleKey = ROLES.includes(params.get('role')) ? params.get('role') : null;
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(null);
  const role = useMemo(() => (roleKey ? SITE_MAP[roleKey] : null), [roleKey]);

  useEffect(() => { setIndex(0); }, [roleKey]);

  const pickRole = (k) => setParams({ role: k });

  const tryIt = async (route) => {
    try {
      const data = await loginByEmail(role.demo.email);
      if (data?.user) {
        setUser(data.user);
        navigate(route || roleHome(data.user.role));
      }
    } catch {
      toast.error('Demo account unavailable right now');
    }
  };

  return (
    <div className="min-h-screen aura-space text-white">
      <Lightbox step={zoom} onClose={() => setZoom(null)} />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <button data-testid="sitemap-back" onClick={() => navigate('/login')} className="text-white/60 hover:text-white text-sm flex items-center gap-2 mb-6"><i className="fa-solid fa-arrow-left" /> Back to sign in</button>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3"><i className="fa-solid fa-map" /> Auralis site map</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-3">How Auralis works,<br className="hidden sm:block" /> for every person who uses it</h1>
          <p className="text-white/60 text-base md:text-lg max-w-3xl">
            Auralis is a precision women’s-health platform connecting patients, clinicians and service administrators. Choose a role below to walk through its journey step by step — what each screen does, and why it is there.
          </p>
        </div>

        <RolePicker active={roleKey} onPick={pickRole} />

        {role ? (
          <div className="mt-8">
            <div className="glass-dark rounded-2xl p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="sitemap-role-summary">
              <div className="flex-1">
                <div className="font-display text-xl text-white mb-1">{role.label}</div>
                <p className="text-white/65 text-sm">{role.summary}</p>
              </div>
              <button
                data-testid="sitemap-open-demo"
                onClick={() => tryIt(null)}
                className="shrink-0 rounded-xl px-4 py-2.5 text-white font-semibold text-sm transition-base"
                style={{ background: role.color }}
              >
                <i className="fa-solid fa-play mr-2" />{role.demo.label}
              </button>
            </div>
            <WalkthroughVideo role={role} />
            <div className="grid lg:grid-cols-4 gap-5">
              <div className="lg:col-span-3">
                <StepPanel role={role} index={index} onIndex={setIndex} onTry={tryIt} onZoom={setZoom} />
              </div>
              <div className="lg:col-span-1">
                <StepOverview role={role} index={index} onIndex={setIndex} />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 glass-dark rounded-2xl p-6 text-white/60 text-sm flex items-center gap-3" data-testid="sitemap-hint">
            <i className="fa-solid fa-hand-pointer text-[var(--aura-purple-light)]" /> Select Patients, Clinicians or Admin above to begin the walkthrough.
          </div>
        )}

        <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="sitemap-principles">
          {[
            { icon: 'fa-user-shield', t: 'Clinician-led, AI-assisted', d: 'AI reads documents and surfaces patterns. Clinicians make every decision and release every result.' },
            { icon: 'fa-lock', t: 'Privacy by design', d: 'Explicit consent, full audit trail, lab documents never stored, data rights built in.' },
            { icon: 'fa-mobile-screen', t: 'Works anywhere', d: 'Installable web app for phone and desktop — no app-store download required.' },
          ].map((p) => (
            <div key={p.t} className="glass-dark rounded-2xl p-5">
              <i className={`fa-solid ${p.icon} text-[var(--aura-purple-light)] mb-2`} />
              <div className="font-display text-lg text-white">{p.t}</div>
              <div className="text-sm text-white/55 mt-1">{p.d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
